import { ChevronDownIcon } from '@heroicons/react/20/solid';
import * as Accordion from '@radix-ui/react-accordion';
import { cs } from '@bbkAdminUtils/css-utils';
import RenderAlignedHtml from '@bbkPricingPage/components/card/components/aligned-html-render';
import { PriceComponent } from '@bbkPricingPage/components/card/components/price-component';
import { type ApplicableAddons } from '@bbkPricingPage/helpers/addon-helper';
import { getEffectiveAmount } from '@bbkPricingPage/helpers/amount-calculator';
import { PricingTableHelper } from '@bbkPricingPage/helpers/pricing-table-helper';
import { getFormattedAmount } from '@bbkPricingPage/lib/currencies';
import { findMatchingFrequency } from '@bbkPricingPage/lib/price-utils';
import {
  type Frequency,
  type Item,
} from '@bbkPricingPage/types/pricing-table-types';

interface AddonsAccordionProps {
  addonSupportEnabled: boolean;
  hideMandatoryAddonPrices: boolean;
  applicableCharges: ApplicableAddons[];
  priceFrequencies: Frequency[];
  itemFrequency: Frequency;
  item: Item;
  applicableAddons: ApplicableAddons[];
}

const AddonsAccordion: React.FC<AddonsAccordionProps> = ({
  addonSupportEnabled,
  hideMandatoryAddonPrices,
  applicableCharges,
  priceFrequencies,
  item,
  itemFrequency,
  applicableAddons,
}) => {
  if (!addonSupportEnabled || item.customization.is_custom_amount_text)
    return null;

  if (!applicableAddons.length && !applicableCharges.length) return null;
  return (
    <div className="tw-w-full">
      <Accordion.Root type="single" collapsible>
        <Accordion.Item value="item-1" className="tw-px-4 tw-pb-4">
          <Accordion.Header className="tw-flex">
            <Accordion.Trigger
              id="accordion-trigger"
              className="tw-flex tw-w-full tw-justify-between tw-px-4 tw-pt-0 focus:tw-outline-none"
            >
              <span className="tw-flex-1 tw-text-left tw-text-base tw-font-semibold tw-text-[var(--pricing-page-text-font-text-color)]">
                {`${applicableAddons.length + applicableCharges.length} ${
                  applicableAddons.length === 1 ? 'Addon' : 'Addons'
                } included`}
              </span>

              <ChevronDownIcon className="[data-state=open]:tw-rotate-180 tw-h-6 tw-w-6 tw-text-[var(--pricing-page-text-font-text-color)] tw-transition-transform tw-duration-200" />
            </Accordion.Trigger>
          </Accordion.Header>

          <Accordion.Content className="data-[state=open]:tw-animate-accordion-down data-[state=closed]:tw-animate-accordion-up tw-overflow-visible">
            <div className="tw-px-4">
              {applicableAddons.map((entry, index) => {
                if (!entry) return null;
                const { addon, price, quantity } = entry;

                const priceFrequency = findMatchingFrequency(
                  price,
                  priceFrequencies
                );
                if (!priceFrequency) return null;

                return (
                  <div
                    key={index}
                    className="tw-my-2 tw-rounded-pricingtable-border-radius tw-border tw-border-[var(--pricingtable-style-accent-color)] tw-bg-[color-mix(in_srgb,var(--pricingtable-style-accent-color)_5%,transparent)] tw-p-4"
                  >
                    <h3 className="tw-pb-2 !tw-text-base tw-font-bold !tw-text-[var(--pricing-page-text-font-text-color)]">
                      {addon.customization?.name}
                    </h3>
                    <span className="tw-pb-1 !tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_60%,transparent)]">
                      <RenderAlignedHtml
                        value={addon.customization?.description ?? ''}
                      />
                    </span>
                    {!hideMandatoryAddonPrices && (
                      <PriceComponent
                        id={price.billing_system_id}
                        price={price}
                        quantity={quantity || 1}
                        frequency={itemFrequency}
                        frequencyOrientation="row"
                        className="tw-items-center tw-justify-start tw-gap-1 tw-text-[var(--pricing-page-text-font-text-color)]"
                        priceClassName="tw-text-left tw-font-bold tw-text-base"
                        suffixClassName="tw-text-left tw-font-normal tw-text-sm"
                        item={item}
                        selectedPlanId={item?.billing_system_id}
                        ignoreEqualHeight={true}
                      />
                    )}
                  </div>
                );
              })}
              {applicableCharges.map((entry, index) => {
                if (!entry) return null;
                const { addon, price, quantity } = entry;
                const chargeAmount = getEffectiveAmount(
                  price,
                  quantity,
                  itemFrequency
                );
                const priceSuffix = PricingTableHelper.generatePriceSuffix(
                  itemFrequency,
                  true
                );
                return (
                  <div
                    key={index}
                    className="tw-my-2 tw-rounded-pricingtable-border-radius tw-border tw-border-[var(--pricingtable-style-accent-color)] tw-bg-[color-mix(in_srgb,var(--pricingtable-style-accent-color)_5%,transparent)] tw-p-4"
                  >
                    <h3 className="tw-pb-1 !tw-text-base tw-font-bold !tw-text-[var(--pricing-page-text-font-text-color)]">
                      {addon.customization?.name}
                    </h3>
                    <span className="tw-mt-0 tw-pb-1 tw-pt-0 !tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_60%,transparent)]">
                      <RenderAlignedHtml
                        value={addon.customization?.description ?? ''}
                      />
                    </span>
                    {!hideMandatoryAddonPrices && (
                      <span className="tw-flex tw-flex-row tw-items-baseline tw-gap-1 tw-pb-1 tw-font-bold !tw-text-[var(--pricing-page-text-font-text-color)]">
                        {getFormattedAmount(chargeAmount, price.currency)}
                        <span
                          id={`frequency-priceSuffix-${itemFrequency.period}-${itemFrequency.period_unit}`}
                          className={cs(
                            'tw-mt-1 tw-text-sm tw-font-medium !tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_60%,transparent)]'
                          )}
                        >
                          {priceSuffix}
                        </span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>
    </div>
  );
};

export default AddonsAccordion;
